import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCheck, Check, Trash2, AlertTriangle, XCircle, Info, Radio, PowerOff, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

// תיבת ההודעות: התראות שהבוט פותח (למשל ערוץ שלא מצליח להתחבר).
// התראה "פתוחה" כל עוד הבעיה קיימת; כשהבעיה חולפת הבוט סוגר אותה לבד.

const FILTERS = [
  { id: 'open', label: 'פתוחות' },
  { id: 'resolved', label: 'נפתרו' },
  { id: 'all', label: 'הכל' }
];

const SEVERITY = {
  error: { icon: XCircle, card: 'border-red-200 bg-red-50', iconColor: 'text-red-600' },
  warning: { icon: AlertTriangle, card: 'border-amber-200 bg-amber-50', iconColor: 'text-amber-600' },
  info: { icon: Info, card: 'border-blue-200 bg-blue-50', iconColor: 'text-blue-600' }
};

const CATEGORY_LABELS = {
  channel: 'ערוצים'
};

// SQLite שומר CURRENT_TIMESTAMP ב-UTC בלי סימון אזור זמן
const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts.replace(' ', 'T') + 'Z');
  return isNaN(d) ? ts : d.toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
};

export default function NotificationsTab({ onNavigate, onCountsChange }) {
  const [filter, setFilter] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { status: filter } });
      setItems(res.data.notifications || []);
      onCountsChange?.({ unread: res.data.unread || 0, open: res.data.open || 0 });
    } catch (e) {
      toast.error('שגיאה בטעינת ההודעות');
    } finally {
      setLoading(false);
    }
  }, [filter, onCountsChange]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (e) {
      toast.error('שגיאה בעדכון');
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      toast.success('הכל סומן כנקרא');
      load();
    } catch (e) {
      toast.error('שגיאה בעדכון');
    }
  };

  const clearResolved = async () => {
    if (!window.confirm('למחוק את כל ההתראות שכבר נפתרו?')) return;
    try {
      const res = await api.delete('/notifications/resolved');
      toast.success(`נמחקו ${res.data.deleted} התראות`);
      load();
    } catch (e) {
      toast.error('שגיאה במחיקה');
    }
  };

  const channelLabel = (n) => n.channel ? `"${n.channel.name || n.channel.username}" (@${n.channel.username})` : '';

  const channelAction = async (n, action) => {
    const what = channelLabel(n);
    const question = action === 'disable'
      ? `להשבית את הערוץ ${what}?\nהבוט יפסיק לנסות להתחבר אליו. אפשר להפעיל אותו שוב בלשונית ניהול ערוצים.`
      : `למחוק את הערוץ ${what} מהפאנל?\nהמחיקה סופית - כל ההגדרות שלו יימחקו.`;
    if (!window.confirm(question)) return;
    try {
      await api.post(`/notifications/${n.id}/channel`, { action });
      toast.success(action === 'disable' ? 'הערוץ הושבת' : 'הערוץ נמחק');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'הפעולה נכשלה');
    }
  };

  const removeNotification = async (n) => {
    const msg = n.resolved_at
      ? 'להסיר את ההתראה מהתיבה?'
      : 'להסיר את ההתראה מהתיבה?\nהערוץ לא משתנה - אם הבעיה נמשכת, ההתראה תחזור בבדיקה הבאה של הבוט.';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/notifications/${n.id}`);
      load();
    } catch (e) {
      toast.error('שגיאה בהסרה');
    }
  };

  const unreadInView = items.some(n => !n.read_at && !n.resolved_at);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">תיבת הודעות</h2>
          <p className="text-sm text-gray-500">התראות על בעיות שהבוט מזהה. התראה נסגרת לבד כשהבעיה נפתרת.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 text-sm ${filter === f.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {unreadInView && (
            <button onClick={markAllRead} className="flex items-center px-3 py-1.5 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">
              <CheckCheck className="w-4 h-4 ml-1" /> סמן הכל כנקרא
            </button>
          )}
          {filter !== 'open' && (
            <button onClick={clearResolved} className="flex items-center px-3 py-1.5 text-sm text-red-700 bg-red-50 hover:bg-red-100 rounded-lg">
              <Trash2 className="w-4 h-4 ml-1" /> מחק שנפתרו
            </button>
          )}
          <button onClick={load} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="רענון">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {!loading && items.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-500">
          {filter === 'open' ? 'אין בעיות פתוחות 🎉' : 'אין התראות'}
        </div>
      )}

      {items.map(n => {
        const sev = SEVERITY[n.severity] || SEVERITY.warning;
        const Icon = sev.icon;
        const resolved = !!n.resolved_at;
        const unread = !n.read_at && !resolved;
        return (
          <div
            key={n.id}
            className={`rounded-lg border p-4 ${resolved ? 'border-gray-200 bg-white opacity-70' : sev.card} ${unread ? 'ring-2 ring-blue-300' : ''}`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${resolved ? 'text-green-600' : sev.iconColor}`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{n.title}</h3>
                  {unread && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white">חדש</span>}
                  {resolved && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">נפתר</span>}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{CATEGORY_LABELS[n.category] || n.category}</span>
                </div>
                {n.category === 'channel' && (
                  <p className="text-sm mt-1">
                    {n.channel ? (
                      <>
                        <span className="text-gray-500">בפאנל: </span>
                        <span className="font-medium text-gray-900">{n.channel.name || '(ללא שם)'}</span>
                        <span className="text-gray-500"> · {n.channel.source_label}</span>
                        {!n.channel.enabled && <span className="mr-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">מושבת</span>}
                      </>
                    ) : (
                      <span className="text-gray-500">הערוץ לא מוגדר ברשימות הערוצים של הפאנל (למשל ערוץ המוזיקה מההגדרות, או שכבר נמחק)</span>
                    )}
                  </p>
                )}
                {n.message && <p className="text-sm text-gray-700 mt-1">{n.message}</p>}
                <p className="text-xs text-gray-500 mt-2">
                  לראשונה: {formatTime(n.first_seen)} · לאחרונה: {formatTime(n.last_seen)}
                  {n.occurrences > 1 && ` · ${n.occurrences} פעמים`}
                  {resolved && ` · נסגר: ${formatTime(n.resolved_at)}${n.resolution ? ` (${n.resolution})` : ''}`}
                </p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {n.category === 'channel' && !resolved && onNavigate && (
                  <button onClick={() => onNavigate('channels')} className="flex items-center px-2 py-1 text-xs text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded">
                    <Radio className="w-3.5 h-3.5 ml-1" /> לניהול ערוצים
                  </button>
                )}
                {unread && (
                  <button onClick={() => markRead(n.id)} className="flex items-center px-2 py-1 text-xs text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 rounded">
                    <Check className="w-3.5 h-3.5 ml-1" /> נקרא
                  </button>
                )}
                {n.channel && !resolved && n.channel.enabled && (
                  <button onClick={() => channelAction(n, 'disable')} className="flex items-center px-2 py-1 text-xs text-amber-800 bg-white border border-amber-300 hover:bg-amber-50 rounded">
                    <PowerOff className="w-3.5 h-3.5 ml-1" /> השבת ערוץ
                  </button>
                )}
                {n.channel && !resolved && (
                  <button onClick={() => channelAction(n, 'delete')} className="flex items-center px-2 py-1 text-xs text-red-700 bg-white border border-red-200 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5 ml-1" /> מחק ערוץ
                  </button>
                )}
                <button onClick={() => removeNotification(n)} className="flex items-center px-2 py-1 text-xs text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded">
                  <X className="w-3.5 h-3.5 ml-1" /> הסר התראה
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
